// Switch / Switch Back on the Monthly page (owner, 4 Nov 2026: "للكل مع اوبشن تخطى فى حالة عدم الرغبة فى السويتش باك
// انما انفيجو السويتش باك اجبارى اول لما العربية تكون جاهزة"). ONE rule for the tabs (workers/contractsStats), the
// table's marks and its WhatsApp menus:
//  * switched: an open contract - any kind - whose own car (the Invygo Plate column: typed by hand, else the car it was
//    opened with) is not the car it has now (the Ejar Plate column: its latest "Create Contract" E-Check);
//  * Mismatch: switched, and its own car can still come back - not while Ejar shows it Rented (with another customer)
//    or gone (owner, 4 Nov 2026, of the 47 such live contracts: "أيوه، تخرج لوحدها"); back to Ready, it shows again;
//  * switch back: its own car is READY (owner, 4 Nov 2026) - Ejar shows it Ready, or the maintenance log has a Date IN
//    after the switch day (typed, or the engine's when Ejar showed it Ready) with no Date OUT after it. Never on
//    the switch day itself (Ejar can show the car Ready for hours after it left, until staff change it - the engine's
//    maintenance_close waits a day for the same reason), and never while Ejar shows it Rented (with another customer)
//    or gone - Sold / Ready To Sell / Stolen (the fleet's one rule, fleetStatus.isGone): the engine closes a sold car's
//    log by itself, and that Date IN is no return (live 4 Nov 2026: 25 of 63 switched contracts' own cars);
//  * an Invygo contract must switch back; any other may skip it (the customer keeps the car they have: it becomes
//    the contract's own car - skipSwitchBackChanges, saved as Edit Mode saves a typed Invygo car).
import { parseSheetDate } from './dates';
import { isInvygoContract } from './contractRows';
import { isGone } from '../../../services/fleetStatus';

const DAY_MS = 86400000;

/** Letters and digits (Arabic kept), upper case: "Y 87613", "y87613" and "Y-87613" are one plate. */
export const plateKey = (plate) => String(plate || '').toUpperCase().replace(/[^A-Z0-9؀-ۿ]/g, '');

/** The contract is on another car than its own. */
export const isSwitched = (row) => {
    const own = plateKey(row?.invygoPlate);
    const now = plateKey(row?.ejarPlate);
    return !!own && !!now && own !== now;
};

/** The own car's Ejar status, lower case ('' unknown). */
const ownStatusOf = (row, statusOf) => String((statusOf && statusOf(row.invygoPlate)) || '').trim().toLowerCase();

/** The own car will not come back: Ejar shows it Rented (another customer) or gone (Sold / Ready To Sell / Stolen). */
const ownCarAway = (status) => status === 'rented' || isGone(status);

/** The Mismatch tab: switched, and its own car not away (`statusOf(plate)` -> its Ejar status). */
export const inMismatch = (row, { statusOf }) => isSwitched(row) && !ownCarAway(ownStatusOf(row, statusOf));

/** An Invygo contract must switch back as soon as its car is ready: no skip. */
export const mustSwitchBack = (row) => isInvygoContract(row);

/** The day the contract went on the car it has now (its Create Contract E-Check), else its pick-up day. */
export const switchDayOf = (row) => parseSheetDate(String(row?.car_now_at || '').slice(0, 10))
    || parseSheetDate(String(row?.pickupDate || row?.['Pick-up Date'] || '').slice(0, 10));

/**
 * A plate's logs -> its latest Date IN (Date) when no later Date OUT sent it back in, else null. An open log (no
 * Date IN) that went in on that day or later is the car in the workshop again (back and out the same day).
 * Worked out once per logs list: the lookups hand the same array for a plate until the log is read again.
 */
const returns = new WeakMap();
const lastReturnOf = (logs) => {
    if (!logs || !logs.length) return null;
    if (!returns.has(logs)) returns.set(logs, readReturn(logs));
    return returns.get(logs);
};
const readReturn = (logs) => {
    let lastIn = null;
    let lastOut = null;
    let lastOpenOut = null;
    logs.forEach((log) => {
        const dIn = parseSheetDate(String(log?.['Date IN'] || ''));
        const dOut = parseSheetDate(String(log?.['Date OUT'] || ''));
        if (dIn && (!lastIn || dIn > lastIn)) lastIn = dIn;
        if (dOut && (!lastOut || dOut > lastOut)) lastOut = dOut;
        if (!String(log?.['Date IN'] || '').trim() && dOut && (!lastOpenOut || dOut > lastOpenOut)) lastOpenOut = dOut;
    });
    if (!lastIn || (lastOut && lastOut > lastIn) || (lastOpenOut && lastOpenOut >= lastIn)) return null;
    return lastIn;
};

/**
 * A switched contract's own car, ready? `logsOf(plate)` -> that car's maintenance logs, `statusOf(plate)` -> its
 * Ejar status ('' unknown), `today` -> the Dubai day (UTC midnight, contractRules.dubaiToday).
 * -> null (not switched / not ready) or { via: 'log' | 'ejar', since: Date | null (the Date IN), days: number | null }.
 */
export const switchBackOf = (row, { logsOf, statusOf, today }) => {
    if (!isSwitched(row)) return null;
    const status = ownStatusOf(row, statusOf);
    if (ownCarAway(status)) return null;
    const day = switchDayOf(row);
    const back = lastReturnOf(logsOf ? logsOf(row.invygoPlate) : []);
    if (back && (!day || back > day)) {
        return { via: 'log', since: back, days: Math.max(0, Math.floor((today - back) / DAY_MS)) };
    }
    if (status === 'ready' && day && day < today) return { via: 'ejar', since: null, days: null };
    return null;
};

/** Skip the switch back (not Invygo): the car the contract has now becomes its own car. */
export const skipSwitchBackChanges = (row) => ({ invygoPlate: row.ejarPlate || '', invygoModel: row.ejarModel || '' });
